// src/services/GeoLogService.ts

import { db } from "../../firebase"; // from your initialized firebase.ts
import { collection, doc, setDoc, getDocs, query, where, Timestamp, collectionGroup, serverTimestamp } from "firebase/firestore";
import { GeoLog, GeoLogSchema } from "../../schemas";
import { create } from "domain";

const COLLECTION = "geo_logs";

export const GeoLogService = {

    async listByUserAccountId(user_account_id: string): Promise<GeoLog[]> {
        const snapshot = await getDocs(
            query(collection(db, COLLECTION), where("user_account_id", "==", user_account_id))
        );

        return snapshot.docs.map((docSnapshot) => {
            const rawData = docSnapshot.data();
            const data = GeoLogSchema.parse({
                ...rawData,
                id: docSnapshot.id,
                created_at: rawData.created_dtm.toDate?.() ?? rawData.created_dtm,
                expires_at: rawData.expires_at.toDate?.() ?? rawData.expires_at,
                tracking_uuid: rawData.tracking_uuid,
            });

            return data;
        });
    },

    async listByUserAndEmployee(user_account_id: string, emp_account_id: string): Promise<GeoLog[]> {
        const logsCollectionRef = collection(db, COLLECTION, user_account_id, "employees", emp_account_id, "logs");

        const snapshot = await getDocs(logsCollectionRef);

        return snapshot.docs.map((docSnapshot) => {
            const rawData = docSnapshot.data();
            return GeoLogSchema.parse({
                ...rawData,
                id: docSnapshot.id,
                user_account_id,
                emp_account_id,
                created_at: rawData.created_dtm.toDate?.() ?? rawData.created_dtm,
                expires_at: rawData.expires_at.toDate?.() ?? rawData.expires_at,
                tracking_uuid: rawData.tracking_uuid,
            });
        });
    },

    async getUserAccountIds(): Promise<string[]> {
        try {
            // Query the top-level collection to get all user account IDs
            const snapshot = await getDocs(collection(db, COLLECTION));
            console.log(
                "User account IDs:",
                snapshot.docs.map((doc) => doc.id)
            );
            // Extract unique user account IDs from the documents
            const userIds = snapshot.docs.map((doc) => doc.id);

            return userIds;
        } catch (error) {
            console.error("Error fetching user account IDs:", error);
            return [];
        }
    },

    async getEmployeeAccountIds(user_account_id: string): Promise<string[]> {
        try {
            if (!user_account_id) return [];

            // Query the employees subcollection for the specified user
            const employeesRef = collection(db, COLLECTION, user_account_id, "employees");
            const snapshot = await getDocs(employeesRef);

            // Extract employee account IDs from the documents
            const employeeIds = snapshot.docs.map((doc) => doc.id);

            return employeeIds;
        } catch (error) {
            console.error(`Error fetching employee account IDs for user ${user_account_id}:`, error);
            return [];
        }
    },

    async getAllByTrackingUuid(
        tracking_uuid: string,
        user_account_id: string,
        emp_account_id: string
    ): Promise<GeoLog[]> {
        const logsCollectionRef = collection(db, COLLECTION, user_account_id, "employees", emp_account_id, "logs");
        console.log("Logs Collection Reference:", logsCollectionRef.path);

        const snapshot = await getDocs(query(logsCollectionRef, where("tracking_uuid", "==", tracking_uuid)));
        console.log("Snapshot Data:", snapshot.docs.map(doc => doc.data()));
        return snapshot.docs.map((docSnapshot) => {
            console.log("Document ID:", docSnapshot.id);
            console.log("Document Data:", docSnapshot.data());
            const rawData = docSnapshot.data();
            console.log("Raw Data:", rawData);

            // Sanitize created_at and expires_at to always be Date objects
            const sanitizeToDate = (value: any): Date | undefined => {
                if (!value) return undefined;
                if (value instanceof Date) return value;
                if (typeof value.toDate === "function") return value.toDate(); // Firestore Timestamp
                if (typeof value === "string" || typeof value === "number") return new Date(value);
                return undefined;
            };

            return GeoLogSchema.parse({
                ...rawData,
                created_at: sanitizeToDate(rawData.created_at),
                expires_at: sanitizeToDate(rawData.expires_at),
            });
        });
    },
};
